/* AdminBillingHealth — operator dashboard for spotting stuck checkouts
   before customers report them. Shows: paid activations, pending
   confirmations, sessions older than 5 min that never activated
   (the "stuck" bucket — most actionable), failed sessions, and recent
   activity. Each row deep-links to /api/admin/billing/debug/{session_id}
   for a full forensic dump and a one-click reconcile button when
   appropriate.

   Backend: GET /api/admin/billing/health?hours=24
            POST /api/admin/billing/reconcile/{session_id}
*/
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCcw, AlertTriangle, CheckCircle2, Clock, XCircle, ExternalLink } from 'lucide-react';
import { Navbar, Footer } from '../components/Navbar';
import { api, formatApiError } from '../lib/api';

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
}

function fmtAge(seconds) {
  if (!Number.isFinite(seconds)) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

function fmtMoney(cents, currency = 'usd') {
  if (cents == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    }).format(cents);
  } catch (_) {
    return `$${cents}`;
  }
}

function StatCard({ label, value, intent, testid }) {
  const tone = {
    success: 'border-emerald-500/40 bg-emerald-50/40 text-emerald-800',
    warning: 'border-amber-500/50 bg-amber-50/50 text-amber-900',
    danger: 'border-red-500/40 bg-red-50/40 text-red-800',
    neutral: 'border-[hsl(var(--primary)/0.12)] bg-white text-[hsl(var(--primary))]',
  }[intent || 'neutral'];
  return (
    <div className={`ml-card !p-5 border ${tone}`} data-testid={testid}>
      <div className="text-[11px] tracking-widest uppercase opacity-80">{label}</div>
      <div className="font-serif text-3xl mt-1">{value}</div>
    </div>
  );
}

function StatusPill({ row }) {
  const ps = row.payment_status || 'pending';
  if (ps === 'paid') {
    return <span className="text-[10px] tracking-widest uppercase font-semibold text-emerald-700 bg-emerald-50 border border-emerald-300/60 rounded-full px-2 py-0.5">Paid</span>;
  }
  if (row.status === 'expired' || ps === 'failed') {
    return <span className="text-[10px] tracking-widest uppercase font-semibold text-red-700 bg-red-50 border border-red-300/60 rounded-full px-2 py-0.5">Failed</span>;
  }
  if (row.age_sec > 300) {
    return <span className="text-[10px] tracking-widest uppercase font-semibold text-amber-800 bg-amber-50 border border-amber-300/70 rounded-full px-2 py-0.5">Stuck</span>;
  }
  return <span className="text-[10px] tracking-widest uppercase font-semibold text-[hsl(var(--primary))] bg-[hsl(var(--muted))] border border-[hsl(var(--primary)/0.15)] rounded-full px-2 py-0.5">Pending</span>;
}

function SyncButton({ sessionId, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!sessionId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/billing/sync-from-stripe/${sessionId}`);
      const refunded = (data.total_refunded_cents || 0) / 100;
      const parts = [
        `Session: ${data.session_status || '—'} / ${data.session_payment_status || '—'}`,
        data.subscription_status ? `Sub: ${data.subscription_status}` : null,
        refunded > 0 ? `Refunds: $${refunded.toFixed(2)}` : null,
      ].filter(Boolean);
      toast.success(`Synced — ${parts.join(' · ')}`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={run}
      disabled={busy}
      className="text-[11px] uppercase tracking-widest font-semibold text-blue-700 hover:underline disabled:opacity-50"
      data-testid={`sync-${sessionId}`}
      title="Pull current state from Stripe (refunds, subscription status) and update MyLeaf's DB to match."
    >
      {busy ? 'Syncing…' : 'Sync'}
    </button>
  );
}

function ReconcileButton({ sessionId, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!sessionId) return;
    if (!window.confirm(`Reconcile session ${sessionId}? This will check Stripe and, if the charge is confirmed there, flip the DB to paid and activate the memorial.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/billing/reconcile/${sessionId}`);
      if (data.action === 'reconciled') {
        toast.success('Reconciled — memorial activated.');
      } else {
        toast.info(data.reason || 'No action needed.');
      }
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={run}
      disabled={busy}
      className="text-[11px] uppercase tracking-widest font-semibold text-[hsl(var(--accent))] hover:underline disabled:opacity-50"
      data-testid={`reconcile-${sessionId}`}
    >
      {busy ? 'Reconciling…' : 'Reconcile'}
    </button>
  );
}

function RefundButton({ row, onDone }) {
  const [busy, setBusy] = useState(false);
  const sid = row.session_id;
  const run = async () => {
    if (!sid) return;
    const note = window.prompt(`Refund session ${sid}?\n\nCustomer: ${row.email || '—'}\nMemorial: ${row.memorial_id || '—'}\nAmount paid: $${row.amount?.toFixed?.(2) ?? row.amount ?? '?'}\n\nEnter an audit note (min 10 chars):`, '');
    if (!note || note.trim().length < 10) return;
    const reasonRaw = window.prompt('Reason — type one of:\n  • duplicate\n  • fraudulent\n  • requested_by_customer\n\n(leave blank to omit)', 'duplicate');
    const reason = (reasonRaw || '').trim();
    const cancelSub = window.confirm('Also CANCEL the Stripe subscription so the $12/yr keeping fee does NOT renew next year?\n\nOK = cancel subscription · Cancel = keep subscription active');
    const after = window.confirm('After refund, also DEACTIVATE the memorial (lock QR + public page)?\n\nOK = deactivate memorial · Cancel = keep memorial active (use for duplicate-charge cleanup)') ? 'deactivate' : 'keep';
    setBusy(true);
    try {
      const body = { note: note.trim(), after_refund: after, cancel_subscription: cancelSub };
      if (reason) body.reason = reason;
      const { data } = await api.post(`/admin/billing/refund/${sid}`, body);
      const refundedMsg = data.refunded
        ? `Refunded $${((data.amount_cents || 0) / 100).toFixed(2)} (${data.status})`
        : 'No refund needed ($0 session)';
      const subMsg = data.subscription_cancelled?.status === 'canceled'
        ? ' · subscription cancelled'
        : data.subscription_cancelled?._error
          ? ` · sub cancel failed: ${data.subscription_cancelled._error.slice(0, 60)}`
          : '';
      toast.success(`${refundedMsg}${subMsg} · memorial ${data.memorial_after}`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={run}
      disabled={busy}
      className="text-[11px] uppercase tracking-widest font-semibold text-red-700 hover:underline disabled:opacity-50"
      data-testid={`refund-${sid}`}
    >
      {busy ? 'Refunding…' : 'Refund'}
    </button>
  );
}

function DebugButton({ sessionId }) {
  const view = async () => {
    if (!sessionId) return;
    try {
      const { data } = await api.get(`/admin/billing/debug/${sessionId}`);
      const w = window.open('', '_blank', 'width=900,height=700');
      if (w) {
        w.document.title = `Billing Debug — ${sessionId.slice(0, 18)}…`;
        w.document.body.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, monospace';
        w.document.body.style.padding = '24px';
        w.document.body.style.fontSize = '12px';
        w.document.body.style.whiteSpace = 'pre-wrap';
        w.document.body.textContent = JSON.stringify(data, null, 2);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <button
      onClick={view}
      className="text-[11px] uppercase tracking-widest font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1"
      data-testid={`debug-${sessionId}`}
    >
      Debug <ExternalLink className="w-3 h-3" />
    </button>
  );
}

function Row({ row, refresh }) {
  const sid = row.session_id || '';
  return (
    <tr className="border-b border-[hsl(var(--primary)/0.06)] hover:bg-[hsl(var(--muted))]/40">
      <td className="py-3 pr-4">
        <StatusPill row={row} />
        {row.webhook_received && (
          <div className="text-[9px] tracking-widest uppercase text-emerald-700/80 mt-1">Webhook ✓</div>
        )}
      </td>
      <td className="py-3 pr-4 font-mono text-xs text-[hsl(var(--primary)/0.7)] break-all max-w-[220px]">{sid.slice(0, 22)}…</td>
      <td className="py-3 pr-4 text-xs">
        <div className="text-[hsl(var(--primary))]">{row.email || '—'}</div>
        {row.memorial_id && <div className="text-[hsl(var(--muted-foreground))] text-[10px]">{row.memorial_id}</div>}
      </td>
      <td className="py-3 pr-4 text-xs">
        <div>{row.plan_id || '—'}</div>
        {row.promo_code && (
          <div className="text-[10px] text-[hsl(var(--accent))]">{row.promo_code} · {row.discount_percent}% off setup</div>
        )}
      </td>
      <td className="py-3 pr-4 text-xs font-mono">
        ${row.amount?.toFixed?.(2) ?? row.amount ?? '—'}
        {row.original_amount && row.original_amount !== row.amount && (
          <div className="text-[10px] line-through text-[hsl(var(--muted-foreground))]">${row.original_amount.toFixed(2)}</div>
        )}
      </td>
      <td className="py-3 pr-4 text-xs text-[hsl(var(--muted-foreground))]">{fmtTime(row.created_at)}</td>
      <td className="py-3 pr-4 text-xs font-mono">{fmtAge(row.age_sec)}</td>
      <td className="py-3 pr-2 text-right whitespace-nowrap">
        <div className="flex items-center justify-end gap-3">
          {row.payment_status !== 'paid' && row.status !== 'expired' && (
            <ReconcileButton sessionId={sid} onDone={refresh} />
          )}
          {row.payment_status === 'paid' && (
            <RefundButton row={row} onDone={refresh} />
          )}
          <SyncButton sessionId={sid} onDone={refresh} />
          <DebugButton sessionId={sid} />
        </div>
      </td>
    </tr>
  );
}

function Section({ title, icon: Icon, rows, intent, refresh, emptyText, testid }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${intent === 'warning' ? 'text-amber-700' : intent === 'danger' ? 'text-red-600' : intent === 'success' ? 'text-emerald-700' : 'text-[hsl(var(--primary))]'}`} />
        <h2 className="font-serif text-xl text-[hsl(var(--primary))]">{title}</h2>
        <span className="ml-2 text-xs text-[hsl(var(--muted-foreground))]">({rows.length})</span>
      </div>
      {rows.length === 0 ? (
        <div className="ml-card !p-6 text-sm text-[hsl(var(--muted-foreground))] italic">{emptyText}</div>
      ) : (
        <div className="ml-card !p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--primary)/0.08)]">
                <th className="py-2 pl-4 pr-4">Status</th>
                <th className="py-2 pr-4">Session</th>
                <th className="py-2 pr-4">Customer</th>
                <th className="py-2 pr-4">Plan</th>
                <th className="py-2 pr-4">Amount</th>
                <th className="py-2 pr-4">Created</th>
                <th className="py-2 pr-4">Age</th>
                <th className="py-2 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row key={r.session_id || r.transaction_id} row={r} refresh={refresh} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function TraceAndActivate({ refresh }) {
  const [traceKey, setTraceKey] = useState('');
  const [traceMode, setTraceMode] = useState('memorial_id');
  const [busy, setBusy] = useState(false);

  const runTrace = async () => {
    if (!traceKey.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get('/admin/billing/trace', { params: { [traceMode]: traceKey.trim() } });
      const w = window.open('', '_blank', 'width=1100,height=900');
      if (w) {
        w.document.title = `Trace — ${traceMode}=${traceKey}`;
        w.document.body.style.cssText = 'font-family:ui-monospace,SFMono-Regular,Menlo,monospace;padding:24px;font-size:12px;white-space:pre-wrap';
        w.document.body.textContent = JSON.stringify(data, null, 2);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runForceActivate = async () => {
    const mid = window.prompt('Memorial ID to force-activate:');
    if (!mid || !mid.trim()) return;
    const reason = window.prompt(`Why are you force-activating ${mid.trim()}?\n\nPaste the Stripe charge proof if you have it (charge_id / session_id / receipt URL).\n\nMin 10 characters — audit-logged.`, '');
    if (!reason || reason.trim().length < 10) return;
    const sid = window.prompt('Linked Stripe session_id (optional, paste cs_live_... if known):', '') || '';
    try {
      const body = { reason: reason.trim() };
      if (sid.trim()) body.session_id = sid.trim();
      const { data } = await api.post(`/admin/memorials/${mid.trim()}/force-activate`, body);
      toast.success(data.activated ? `Memorial ${mid.trim()} ACTIVATED.` : `Memorial ${mid.trim()} was already active — audit row written.`);
      refresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="ml-card !p-4 mt-6 border-2 border-red-200 bg-red-50/30" data-testid="trace-bar">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-red-700">Emergency forensic tools</div>
        <select
          value={traceMode}
          onChange={(e) => setTraceMode(e.target.value)}
          className="ml-input !py-2"
          data-testid="trace-mode"
        >
          <option value="memorial_id">memorial_id</option>
          <option value="session_id">session_id</option>
          <option value="user_id">user_id</option>
          <option value="email">email</option>
        </select>
        <input
          value={traceKey}
          onChange={(e) => setTraceKey(e.target.value)}
          placeholder={`Enter ${traceMode}…`}
          className="ml-input flex-1 min-w-[260px]"
          data-testid="trace-input"
          onKeyDown={(e) => { if (e.key === 'Enter') runTrace(); }}
        />
        <button
          onClick={runTrace}
          disabled={busy || !traceKey.trim()}
          className="ml-btn-outline"
          data-testid="trace-btn"
        >
          {busy ? 'Tracing…' : 'Trace'}
        </button>
        <button
          onClick={runForceActivate}
          className="ml-btn-gold !bg-red-600 !border-red-600 hover:!bg-red-700"
          data-testid="force-activate-btn"
        >
          Force-activate a memorial
        </button>
      </div>
      <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed">
        <strong>Trace</strong> opens a full forensic dump (DB txns + live Stripe state per session + audit). <strong>Force-activate</strong> manually marks a memorial paid when Stripe charged but our flow didn&apos;t fire — requires a 10+ char reason for audit.
      </div>
    </div>
  );
}

export default function AdminBillingHealth() {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(true);
  const [dupSubs, setDupSubs] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, dupResp] = await Promise.all([
        api.get('/admin/billing/health', { params: { hours } }),
        api.get('/admin/billing/duplicate-subs').catch(() => ({ data: null })),
      ]);
      setData(d);
      setDupSubs(dupResp?.data || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s so stuck sessions surface without manual refresh.
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const counts = data?.counts || { recent: 0, paid: 0, pending: 0, failed: 0, stuck: 0 };
  const recent = data?.recent || [];
  const stuckRows = data?.stuck || [];
  const paidRows = recent.filter((r) => r.payment_status === 'paid');
  const failedRows = recent.filter((r) => r.status === 'expired' || r.payment_status === 'failed');
  const pendingRows = recent.filter((r) => r.payment_status !== 'paid' && r.status !== 'expired' && r.payment_status !== 'failed');

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container py-10 max-w-6xl">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <Link to="/admin/accounts" className="ml-btn-link inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]" data-testid="back-link">
              <ArrowLeft className="w-4 h-4" /> Back to admin
            </Link>
            <h1 className="font-serif text-4xl text-[hsl(var(--primary))] mt-2">Billing Health</h1>
            <p className="ml-caption text-sm mt-2 max-w-xl">
              Spot stuck checkouts before customers report them. Auto-refreshes every 30 seconds.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                if (!window.confirm('Run Stripe self-test? Returns a JSON snapshot of the deployed Stripe config + a live auth+price test.')) return;
                try {
                  const { data } = await api.get('/admin/billing/selftest');
                  const w = window.open('', '_blank', 'width=900,height=800');
                  if (w) {
                    w.document.title = `Stripe Self-Test (${data.code_version})`;
                    w.document.body.style.cssText = 'font-family:ui-monospace,SFMono-Regular,Menlo,monospace;padding:24px;font-size:12px;white-space:pre-wrap';
                    w.document.body.textContent = JSON.stringify(data, null, 2);
                  }
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="ml-btn-outline inline-flex items-center gap-2"
              data-testid="selftest-btn"
            >
              Run Stripe self-test
            </button>
            <button
              onClick={async () => {
                if (!window.confirm('Reconcile EVERY pending session right now? This refetches each session from Stripe and activates the memorial if Stripe confirms payment. Idempotent.')) return;
                try {
                  const { data } = await api.post('/admin/billing/reconcile-all-pending');
                  toast.success(`Checked ${data.checked} sessions · ${data.activated} activated · ${data.skipped} not paid yet · ${data.errored} errored`);
                  load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="ml-btn-outline inline-flex items-center gap-2"
              data-testid="reconcile-all-btn"
            >
              Reconcile all pending
            </button>
            <select
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              className="ml-input !py-2"
              data-testid="window-select"
            >
              <option value={1}>Last 1 hour</option>
              <option value={6}>Last 6 hours</option>
              <option value={24}>Last 24 hours</option>
              <option value={72}>Last 3 days</option>
              <option value={168}>Last 7 days</option>
            </select>
            <button
              onClick={load}
              disabled={loading}
              className="ml-btn-outline inline-flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <TraceAndActivate refresh={load} />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
          <StatCard label="Paid activations" value={counts.paid} intent="success" testid="stat-paid" />
          <StatCard label="Pending" value={counts.pending - counts.stuck} intent="neutral" testid="stat-pending" />
          <StatCard label="Stuck > 5 min" value={counts.stuck} intent={counts.stuck > 0 ? 'warning' : 'neutral'} testid="stat-stuck" />
          <StatCard label="Failed" value={counts.failed} intent={counts.failed > 0 ? 'danger' : 'neutral'} testid="stat-failed" />
        </div>

        {dupSubs && dupSubs.count > 0 && (
          <div className="ml-card !p-5 mt-6 border-2 border-amber-400 bg-amber-50" data-testid="duplicate-subs-warning">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
              <h2 className="font-serif text-xl text-amber-900">
                {dupSubs.count} memorial{dupSubs.count > 1 ? 's' : ''} with multiple paid sessions
              </h2>
            </div>
            <p className="text-sm text-amber-900/80 mb-4 leading-relaxed">
              The same memorial was charged more than once. Refund the duplicate sessions and (where applicable) cancel the extra Stripe subscriptions so the keeping fee doesn&apos;t double-renew next year. Use <em>Trace</em> on the memorial_id to see every session and Refund the duplicates inline (uncheck &ldquo;deactivate memorial&rdquo; so the original activation stays in place).
            </p>
            <div className="space-y-3">
              {dupSubs.duplicate_memorials.map((d) => (
                <div key={d.memorial_id} className="bg-white border border-amber-300 rounded-md p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-serif text-lg text-[hsl(var(--primary))]">{d.memorial?.full_name || d.memorial_id}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 font-mono">{d.memorial_id}</div>
                      <div className="text-xs mt-1">
                        <strong>{d.paid_session_count}</strong> paid sessions · total <strong>${d.total_amount_paid?.toFixed(2)}</strong> · status <strong>{d.memorial?.plan_status || '—'}</strong>
                      </div>
                    </div>
                    <div className="text-[11px] font-mono text-[hsl(var(--muted-foreground))] break-all max-w-md space-y-1">
                      {d.session_ids.map((s) => (
                        <div key={s} className="flex items-center gap-2">
                          <SyncButton sessionId={s} onDone={load} />
                          <span>{s}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-10 mt-10">
          {counts.stuck > 0 && (
            <Section
              title="Stuck checkouts — needs attention"
              icon={AlertTriangle}
              rows={stuckRows}
              intent="warning"
              refresh={load}
              emptyText="No stuck sessions."
              testid="section-stuck"
            />
          )}
          <Section
            title="Pending confirmations"
            icon={Clock}
            rows={pendingRows.filter((r) => r.age_sec <= 300)}
            intent="neutral"
            refresh={load}
            emptyText="No pending sessions — clean pipeline."
            testid="section-pending"
          />
          <Section
            title="Paid activations"
            icon={CheckCircle2}
            rows={paidRows.slice(0, 20)}
            intent="success"
            refresh={load}
            emptyText="No paid sessions in this window."
            testid="section-paid"
          />
          {failedRows.length > 0 && (
            <Section
              title="Failed / expired"
              icon={XCircle}
              rows={failedRows}
              intent="danger"
              refresh={load}
              emptyText="No failed sessions."
              testid="section-failed"
            />
          )}
        </div>

        <p className="text-center text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.4)] mt-12">
          {data?.checked_at ? `Last checked ${fmtTime(data.checked_at)}` : 'Loading…'}
        </p>
      </div>
      <Footer />
    </div>
  );
}
